import React, { useCallback, useEffect, useMemo, useState } from "react";
import { todayStr, startOfMonth, RANGE_PRESETS, activePresetKey } from "../../utils/dateRange";
import { logActivity } from "../../utils/activity";
import { buildCsv, downloadCsv, locSlug } from "../../utils/csv";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

function api(path) {
    const token = localStorage.getItem("accessToken");
    return fetch(`${GAMES_CLOUD_BASE}${path}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
}

// Scoreboard is the "Scoreboard" admin tab: the combined points per GM (checklist +
// afslutninger + kundefeedback + mersalg) over a chosen period, with the per-source
// breakdown, plus team totals. Unlike the GM-facing page, every row is shown (no top 10).
export default function Scoreboard({ config }) {
    const locationKeys = useMemo(() => Object.keys(config?.locations || {}), [config]);
    const [location, setLocation] = useState(""); // "" = all locations
    const [from, setFrom] = useState(startOfMonth());
    const [to, setTo] = useState(todayStr());
    const [scores, setScores] = useState([]);
    const [teamScores, setTeamScores] = useState([]);
    const [loading, setLoading] = useState(false);

    const locationName = key => config?.locations?.[key]?.displayName || key;

    function applyPreset(p) {
        setFrom(p.from());
        setTo(todayStr());
    }
    const activePreset = activePresetKey(from, to);

    // Reload whenever a filter changes — no explicit "show" button needed.
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api(`scoreboard?from=${from}&to=${to}&location=${encodeURIComponent(location)}`);
            if (r.ok) {
                const data = await r.json();
                setScores(data.scores || []);
                setTeamScores(data.team_scores || []);
            }
        } finally {
            setLoading(false);
        }
    }, [from, to, location]);

    useEffect(() => { load(); }, [load]);

    function exportCsv() {
        if (!scores.length) { alert("Ingen data at eksportere."); return; }
        const header = ["Placering", "GM", "MedarbejderID", "Point i alt", "Checkliste", "Afslutninger", "Feedback", "Mersalg", "Spil", "Feedbacks"];
        const rows = scores.map((s, i) => [
            i + 1, s.name, s.employee_id, s.points,
            s.checklist_points, s.report_points, s.feedback_points, s.upsell_points,
            s.games, s.feedback_count,
        ]);
        const loc = locSlug(location ? locationName(location) : "alle-lokationer");
        downloadCsv(`scoreboard_${loc}_${from}_${to}.csv`, buildCsv(header, rows));
        logActivity("scoreboard_export", `${loc} ${from}..${to}`);
    }

    return (
        <>
            <div className="acl-scope">
                <label>Lokation
                    <select value={location} onChange={e => setLocation(e.target.value)}>
                        <option value="">Alle lokationer</option>
                        {locationKeys.map(k => <option key={k} value={k}>{locationName(k)}</option>)}
                    </select>
                </label>
                <label>Fra<input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
                <label>Til<input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
                <div className="acl-presets">
                    {RANGE_PRESETS.map(p => (
                        <button key={p.key}
                            className={`acl-preset ${activePreset === p.key ? "active" : ""}`}
                            onClick={() => applyPreset(p)}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            <section className="acl-card">
                <div className="acl-card-head">
                    <h2>Point-scoreboard</h2>
                    <button className="acl-preset" onClick={exportCsv}>Download CSV</button>
                </div>
                <p className="acl-hint">
                    Point i alt = checkliste + afslutninger + kundefeedback + mersalg. Satserne sættes under fanen "Point".
                </p>
                <table className="acl-table">
                    <thead>
                        <tr>
                            <th>#</th><th>GM</th><th>Point i alt</th>
                            <th>Checkliste</th><th>Afslutninger</th><th>Feedback</th><th>Mersalg</th>
                            <th>Spil</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scores.length === 0 && <tr><td colSpan={8} className="acl-dim">{loading ? "Henter…" : "Ingen data for perioden."}</td></tr>}
                        {scores.map((s, i) => (
                            <tr key={s.name}>
                                <td>{i + 1}</td>
                                <td>{s.name}</td>
                                <td><strong>{s.points}</strong></td>
                                <td className="acl-dim">{s.checklist_points}</td>
                                <td className="acl-dim">{s.report_points}</td>
                                <td className="acl-dim">{s.feedback_points}</td>
                                <td className="acl-dim">{s.upsell_points}</td>
                                <td className="acl-dim">{s.games}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            {teamScores.length > 0 && (
                <section className="acl-card">
                    <h2>Teams</h2>
                    <table className="acl-table">
                        <thead><tr><th>#</th><th>Team</th><th>Point</th><th>Medlemmer</th></tr></thead>
                        <tbody>
                            {teamScores.map((t, i) => (
                                <tr key={t.id}>
                                    <td>{i + 1}</td>
                                    <td>{t.name}</td>
                                    <td><strong>{t.points}</strong></td>
                                    <td className="acl-dim">{t.members.join(", ") || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}
        </>
    );
}
